/**
 * AnimatedPrice Component
 *
 * Displays price with optional original price strike-through animation.
 * Features:
 * - Fade in animation for prices
 * - Strike-through line draws across original price
 * - Scale pop effect for sale price
 * - Configurable colors and sizes
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

/**
 * PAngV (Preisangabenverordnung) price disclaimer translations
 * "inkl. MwSt., zzgl. Versand" - EU price indication regulation
 */
export const PRICE_DISCLAIMER: Record<string, string> = {
  de: 'inkl. MwSt., zzgl. Versand',
  en: 'incl. VAT, plus shipping',
  it: 'IVA inclusa, spese di spedizione escluse',
  el: 'συμπ. ΦΠΑ, πλέον μεταφορικά',
  pt: 'IVA incluído, portes não incluídos',
  fr: 'TVA incluse, frais de port en sus',
  es: 'IVA incluido, gastos de envío no incluidos',
  nl: 'incl. BTW, excl. verzendkosten',
  pl: 'z VAT, plus koszty wysyłki',
  tr: 'KDV dahil, kargo hariç',
  ru: 'вкл. НДС, плюс доставка',
  ja: '税込、送料別',
  ko: 'VAT 포함, 배송비 별도',
  zh: '含增值税，另加运费',
  ar: 'شامل ضريبة القيمة المضافة، بالإضافة إلى الشحن',
  hi: 'VAT सहित, शिपिंग अतिरिक्त',
  sv: 'inkl. moms, plus frakt',
  da: 'inkl. moms, plus fragt',
  no: 'inkl. mva, pluss frakt',
  fi: 'sis. ALV, toimituskulut lisäksi',
  cs: 'vč. DPH, plus doprava',
  ro: 'TVA inclus, plus livrare',
  hu: 'ÁFA-val, plusz szállítás',
  uk: 'з ПДВ, плюс доставка',
  bg: 'с ДДС, плюс доставка',
  hr: 's PDV-om, plus dostava',
  th: 'รวม VAT บวกค่าจัดส่ง',
};

export interface AnimatedPriceProps {
  originalPrice?: string;
  salePrice: string;
  /** Frame when animation should start */
  startFrame?: number;
  /** Color for the sale price */
  salePriceColor?: string;
  /** Color for the original price */
  originalPriceColor?: string;
  /** Font size for sale price */
  salePriceFontSize?: number;
  /** Font size for original price */
  originalPriceFontSize?: number;
  /** Font family */
  fontFamily?: string;
  /** Layout direction */
  direction?: 'row' | 'column';
  /** Gap between prices */
  gap?: number;
  /** Show PAngV disclaimer below price */
  showPriceDisclaimer?: boolean;
  /** Language for disclaimer text */
  language?: string;
}

export const AnimatedPrice: React.FC<AnimatedPriceProps> = ({
  originalPrice,
  salePrice,
  startFrame = 0,
  salePriceColor = '#FF6B35',
  originalPriceColor = 'rgba(255,255,255,0.5)',
  salePriceFontSize = 48,
  originalPriceFontSize = 32,
  fontFamily = 'Inter, sans-serif',
  direction = 'row',
  gap = 20,
  showPriceDisclaimer = false,
  language = 'de',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Original price fade in (first)
  const originalOpacity = interpolate(
    relativeFrame,
    [0, fps * 0.3],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Strike-through animation (draws from left to right)
  const strikeWidth = interpolate(
    relativeFrame,
    [fps * 0.3, fps * 0.6],
    [0, 100],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Sale price animation (pop in after strike-through)
  const saleOpacity = interpolate(
    relativeFrame,
    [fps * 0.5, fps * 0.7],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  const saleScale = spring({
    frame: relativeFrame - fps * 0.5,
    fps,
    from: 0.5,
    to: 1,
    durationInFrames: fps * 0.4,
    config: {
      stiffness: 200,
      damping: 12,
    },
  });

  // Disclaimer opacity - fades in after sale price
  const disclaimerOpacity = interpolate(
    relativeFrame,
    [fps * 0.8, fps * 1.0],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Price Row */}
      <div
        style={{
          display: 'flex',
          flexDirection: direction,
          alignItems: 'center',
          justifyContent: 'center',
          gap,
        }}
      >
        {/* Original Price with Strike-through */}
        {originalPrice && (
          <div
            style={{
              position: 'relative',
              opacity: originalOpacity,
            }}
          >
            <span
              style={{
                color: originalPriceColor,
                fontSize: originalPriceFontSize,
                fontFamily,
                fontWeight: 500,
              }}
            >
              {originalPrice}
            </span>
            {/* Animated strike-through line */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                width: `${strikeWidth}%`,
                height: 3,
                backgroundColor: originalPriceColor,
                transform: 'translateY(-50%)',
              }}
            />
          </div>
        )}

        {/* Sale Price */}
        <span
          style={{
            color: salePriceColor,
            fontSize: salePriceFontSize,
            fontFamily,
            fontWeight: 700,
            opacity: saleOpacity,
            transform: `scale(${saleScale})`,
          }}
        >
          {salePrice}
        </span>
      </div>

      {/* PAngV Price Disclaimer */}
      {showPriceDisclaimer && (
        <span
          style={{
            fontSize: salePriceFontSize * 0.35,
            color: 'rgba(255,255,255,0.6)',
            marginTop: 8,
            fontFamily,
            opacity: disclaimerOpacity,
          }}
        >
          {PRICE_DISCLAIMER[language] || PRICE_DISCLAIMER.de}
        </span>
      )}
    </div>
  );
};
